import isEqual from 'lodash/isEqual';
import uniq from 'lodash/uniq';
import uniqWith from 'lodash/uniqWith';

import { GetDestinationsQuery } from 'app/generated/hygraph';
import { createEntitlement } from 'app/services/EntitlementHelper';
import {
  AttractionConfig,
  ConfigurationResponse,
  Entitlement,
  GuestData,
  GuestDataResponse,
  Pass,
  ProductLineup,
  Reservation,
  TicketClass,
  ValidStates,
} from 'app/services/GuestCenterService.types';
import Logger from 'app/services/Logger';
import { createAppProductLineupStub } from 'app/services/ProductLineupHelper';

const MODULE = '[GuestCenterHelpers]';

export function getEntitlements({
  guestDataResponse,
  productLineups,
  attractionConfigs,
  destinationsContent,
}: {
  guestDataResponse: GuestDataResponse | undefined;
  productLineups: ProductLineup[];
  attractionConfigs: AttractionConfig[];
  destinationsContent: GetDestinationsQuery['products'];
}): Entitlement[] {
  const passes = guestDataResponse?.guestData?.passes;

  if (passes) {
    const availableLineupKeys = new Set(productLineups.map((pl) => pl.productLineupKey));
    const invalidPasses = passes.filter((pass) => {
      const lineupKey = pass.productLineupKey;
      return lineupKey && !availableLineupKeys.has(lineupKey);
    });

    if (invalidPasses.length > 0) {
      Logger.warn(`${MODULE} Found passes with productLineupKeys not in configuration`, {
        invalidLineupKeys: invalidPasses.map((p) => p.productLineupKey),
        availableLineupKeys: Array.from(availableLineupKeys),
        passCount: passes.length,
        lineupCount: productLineups.length,
      });
    }

    return uniqWith(
      passes.map((pass) => {
        const productLineup: ProductLineup =
          productLineups.find((pl) => pl.productLineupKey === pass.productLineupKey) ??
          createAppProductLineupStub();

        return {
          locator: pass.locator,
          productLineup,
        };
      }),
      isEqual
    ).map(({ locator, productLineup }) => {
      const travelParty = guestDataResponse?.guestData.travelParties?.find(
        (tp) => tp.locator === locator
      );
      const ent = createEntitlement({
        entitlementKey: locator,
        travelParty,
        passes: guestDataResponse?.guestData.passes ?? [],
        reservations: guestDataResponse?.guestData.reservations ?? [],
        attractionConfigs,
        productLineup,
        destinationsContent,
      });

      Logger.debug(`${MODULE} created entitlement`, {
        entitlementKey: ent.entitlementKey,
        productContentKey: ent.productContentKey,
        passesCount: ent.passes.length,
      });

      return ent;
    });
  }
  return [];
}

export function createGuest({
  email,
  activeEntitlementKey,
  guestDataResponse,
  configurationResponse,
  destinationsContent = [],
}: {
  email: Maybe<string>;
  activeEntitlementKey: Maybe<string>;
  guestDataResponse: GuestDataResponse | undefined;
  configurationResponse: ConfigurationResponse | undefined;
  destinationsContent: GetDestinationsQuery['products'] | undefined;
}) {
  const { passes = [], reservations = [] } = guestDataResponse?.guestData ?? {};
  const productLineups = configurationResponse?.productLineups.configuration ?? [];
  const attractionConfigs = configurationResponse?.attractions.configuration ?? [];
  const entitlements = getEntitlements({
    guestDataResponse,
    productLineups,
    attractionConfigs,
    destinationsContent: destinationsContent ?? [],
  });
  const activeEntitlement =
    entitlements.find((ent) => ent.isValid && ent.entitlementKey === activeEntitlementKey) ?? null;
  const entitlementReservations = entitlements.flatMap((ent) => ent.reservations);

  if (email && guestDataResponse?.guestData?.email && guestDataResponse.guestData.email !== email) {
    const error = `${MODULE} : Creating Guest Context : ${email} with data for ${guestDataResponse.guestData.email}`;
    Logger.warn(error);
  }

  const validEntitlements = entitlements.filter((ent) => ent.isValid);

  return {
    email: email ?? guestDataResponse?.guestData?.email ?? '',
    hasResponse: !!guestDataResponse,
    hasEntitlements: entitlements.length > 0,
    hasMultipleEntitlements: entitlements.length > 1,
    hasPasses: passes.length > 0,
    hasReservations: reservations.length > 0,
    entitlements: validEntitlements,
    allEntitlements: entitlements,
    activeEntitlement,
    reservations: entitlementReservations,
    passes: passes.filter((p) => p.status === ValidStates.Valid),
    allPasses: passes,
    entitlementsQtyCode: getEntitlementsQtyCode(validEntitlements),
    hasActiveEntitlementFor: (productContentKey: Maybe<string>) => {
      if (!productContentKey || !activeEntitlement) return false;
      return activeEntitlement.productContentKey === productContentKey;
    },
    hasEntitlementFor: (productContentKey: Maybe<string>) => {
      if (!productContentKey || !validEntitlements || validEntitlements.length === 0) return false;
      return validEntitlements.some((ent) => ent.productContentKey === productContentKey);
    },
  };
}

function getEntitlementsQtyCode(entitlements: Entitlement[]) {
  switch (entitlements.length) {
    case 0:
      return 'none';
    case 1:
      return 'single';
    default:
      return 'multi';
  }
}

export function getPasses(userDataResponse: GuestDataResponse, ticketClass?: TicketClass): Pass[] {
  if (userDataResponse?.guestData?.passes) {
    if (ticketClass) {
      return userDataResponse.guestData.passes.filter(
        (pass: Pass) => pass.ticketClass === ticketClass
      );
    }
    return userDataResponse.guestData.passes;
  }
  return [];
}

export const getPassCount = (userDataResponse: GuestDataResponse, ticketClass?: TicketClass) => {
  return getPasses(userDataResponse, ticketClass).length;
};

export function getReservations(guestData: GuestData): Reservation[] {
  if (guestData?.reservations) {
    return guestData.reservations;
  }
  return [];
}

export const getReservationsForAttraction = (
  reservations: Reservation[],
  attractionKey: string
): Reservation[] => {
  return reservations.filter(
    (reservation) => reservation.attractionKey.toLowerCase() === attractionKey.toLowerCase()
  );
};

export const getProductKeys = (passes: Pass[]) => {
  return uniq(passes.flatMap((pass) => pass.productLineupKey));
};

export const getLocators = (passes: Pass[]): string[] => {
  return uniq(passes.flatMap((pass) => (pass.locator == null ? [] : [pass.locator])));
};

export const getProductKeyCount = (passes: Pass[]) => {
  return getProductKeys(passes).length;
};

export const getProductLineupCount = (passes: Pass[]) => {
  return getProductKeys(passes).length;
};

export const getTravelPartyCount = (passes: Pass[]) => {
  return getLocators(passes).length;
};

export const findProductLineupForBarcode = (
  barcode: string,
  { productLineup, passes }: { productLineup: ProductLineup[]; passes: Pass[] }
): ProductLineup | undefined => {
  Logger.debug(`${MODULE} findProductLineupForBarcode`, {
    barcode,
    productLineup: productLineup.map((p) => ({
      contentKey: p.contentKey,
      productLineupKey: p.productLineupKey,
      year: p.year,
    })),
    passes: passes.map((p) => ({
      barcode: p.barcode,
      lineupKey: p.productLineupKey,
    })),
  });
  const pass = passes.find(($pass) => $pass.barcode === barcode);
  if (!pass) {
    Logger.warn(`${MODULE} no pass found for barcode`, { barcode });
    return undefined;
  }

  const lineup = productLineup.find(
    ($productLineup) => $productLineup.productLineupKey === pass?.productLineupKey
  );
  if (!lineup) {
    Logger.warn(`${MODULE} no product lineup found for pass`, { pass });
    return undefined;
  }

  return lineup;
};

export const getPassForBarcode = ({ barcode, passes }: { barcode: string; passes: Pass[] }) => {
  const pass = passes.find((p) => p.barcode === barcode);
  if (!pass) Logger.warn(`${MODULE} unable to find pass for barcode`, { barcode, passes });
  return pass;
};

export const getPassesForBarcodes = ({
  barcodes,
  passes,
}: {
  barcodes: string[];
  passes: Pass[];
}) => {
  return barcodes.reduce<Pass[]>((acc, barcode) => {
    const pass = getPassForBarcode({ barcode, passes });
    if (pass) acc.push(pass);
    return acc;
  }, []);
};

export const getReservation = ({
  reservationKey,
  userData,
}: {
  reservationKey: string;
  userData?: GuestDataResponse;
}) => {
  return userData?.guestData.reservations.find((res) => res.reservationKey === reservationKey);
};
